<template>
  <div class="text-static-default-hi">
    <Breadcrumb v-if="!isLoading" :models-names />
    <ModelsComparisonResultsSkeleton v-if="isLoading" class="pt-32" />
    <RevContainer v-else class="bg-surface-default-low pb-56 pt-32 md:pb-72">
      <h1 class="heading-1 text-center">
        {{ title }}
      </h1>
      <Transition
        enter-active-class="ease-out duration-150"
        enter-from-class="-translate-y-full"
        enter-to-class="translate-y-0"
        leave-active-class="ease-in duration-150"
        leave-from-class="translate-y-0"
        leave-to-class="-translate-y-full"
      >
        <div
          v-show="isScrolled"
          class="bg-surface-default-low border-static-default-low sticky left-0 right-0 top-0 z-[1] border-b"
        >
          <div
            v-show="isScrolled"
            class="bg-surface-default-low border-static-default-low sticky left-0 right-0 top-0 z-[1] border-b"
          >
            <div
              class="mx-auto grid grid-cols-2 gap-x-24 pb-12 pt-16 lg:gap-x-40 lg:pb-24"
            >
              <ModelsComparisonProductCard
                v-for="product in products"
                :key="`sticky-card-${product.productId}`"
                :product
                sticky
              />
            </div>
          </div>
        </div>
      </Transition>
      <div class="mx-auto mt-24">
        <div ref="bodyContainer" class="grid grid-cols-2 gap-x-24 lg:gap-x-40">
          <ModelsComparisonProductCard
            v-for="product in products"
            :key="`card-${product.productId}`"
            :product
          />
        </div>
        <ProductComparisonTableContent
          class="mt-16"
          :nb-columns="2"
          :table-data
        />
      </div>
      <div v-if="relatedArticles?.length > 0" class="mt-48 text-left">
        <h2 class="heading-3 mb-16">
          {{ i18n(translations.relatedArticlesTitle) }}
        </h2>

        <ArticleCardsCarousel :articles="relatedArticles" />
      </div>
      <div v-if="relatedComparePages?.length > 0" class="mt-48 text-left">
        <h2 class="heading-3 mb-16">
          {{ i18n(translations.relatedComparePagesTitle) }}
        </h2>

        <ArticleCardsCarousel :articles="relatedComparePages" />
      </div>
    </RevContainer>
  </div>
</template>

<script lang="ts" setup>
import { navigateTo, showError, useAsyncData, useRoute } from '#imports'
import { computed, ref } from 'vue'

import type {
  ModelComparisonCompareProduct,
  ModelComparisonCompareTextBlock,
} from '@backmarket/http-api/src/api-specs-model-comparison/models/compare-models'
import ArticleCardsCarousel from '@backmarket/nuxt-layer-cms/ArticleCard/ArticleCardsCarousel.vue'
import ProductComparisonTableContent from '@backmarket/nuxt-layer-recommendation/ProductComparisonTableContent.vue'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevContainer } from '@ds/components/Container'
import { useIntersectionObserver } from '@vueuse/core'

import { useAddCanonicalLink } from '~/scopes/seo/composables/useAddCanonicalLink'

import Breadcrumb from '../../components/Breadcrumb/Breadcrumb.vue'
import { useFetchModelsComparisonV2 } from '../../composables/useFetchModelComparisonV2'
import { useModelsComparisonHead } from '../../composables/useModelsComparisonHeadV2'
import { MODELS_COMPARISON } from '../../routes-names'

import ModelsComparisonProductCard from './ModelsComparisonProductCard.vue'
import ModelsComparisonResultsSkeleton from './ModelsComparisonResultsSkeleton.vue'
import translations from './ModelsComparisonResultsV2.translations'

type ErrorData = {
  errors?: Array<{
    context?: {
      payload?: { detail?: { error?: { details?: unknown } } }
    }
  }>
}

type RedirectErrorDetails = {
  error_type?: string
  slug_1?: string
  slug_2?: string
}

const $httpFetch = useHttpFetch()
const route = useRoute()

// Fetch comparison data
const {
  data: comparisonData,
  error: comparisonError,
  pending: comparisonPending,
} = await useAsyncData<{
  products: ModelComparisonCompareProduct[]
  textBlocks: ModelComparisonCompareTextBlock[]
}>(
  getAsyncDataKey('models-comparison-v2', {
    slug1: route.params.productA,
    slug2: route.params.productB,
  }),
  () =>
    $httpFetch('/product-page/compare/:model1/:model2', {
      pathParams: {
        model1: route.params.productA as string,
        model2: route.params.productB as string,
      },
    }),
)

// Handle redirect error
if (comparisonError.value) {
  const errorData = (comparisonError.value as { data?: unknown })?.data as
    | ErrorData
    | undefined

  const errorDetails = errorData?.errors?.[0]?.context?.payload?.detail?.error
    ?.details as RedirectErrorDetails | undefined

  if (
    errorDetails?.error_type === 'redirect_to' &&
    errorDetails.slug_1 &&
    errorDetails.slug_2
  ) {
    navigateTo(
      {
        name: MODELS_COMPARISON.COMPARISON_RESULTS,
        params: {
          productA: errorDetails.slug_1,
          productB: errorDetails.slug_2,
        },
      },
      {
        redirectCode: 301,
      },
    )
  } else {
    // Handle other errors
    const errorWithCode = comparisonError.value as {
      statusCode?: number
      statusMessage?: string
    }
    showError({
      statusCode: errorWithCode.statusCode || 500,
      statusMessage: errorWithCode.statusMessage || 'An error occurred',
    })
  }
}

// Use composable to process the data
const { tableData, pending, articlesList } = useFetchModelsComparisonV2(
  comparisonData,
  comparisonPending,
)

const isScrolled = ref(false)

const i18n = useI18n()
const bodyContainer = ref()

const products = computed(() => comparisonData.value?.products ?? [])

const title = computed(() =>
  i18n(translations.resultsTitle, {
    firstModel: products.value?.[0]?.title,
    secondModel: products.value?.[1]?.title,
  }),
)

const isLoading = computed(() => pending.value || !products.value?.length)

const modelsNames = computed(() => products.value?.map((p) => p.title))

const relatedArticles = computed(
  () =>
    articlesList.value?.find((item) => item.listId === 'cp_related_articles')
      ?.articles ?? [],
)

const relatedComparePages = computed(
  () =>
    articlesList.value?.find(
      (item) => item.listId === 'cp_related_compare_pages',
    )?.articles ?? [],
)

useIntersectionObserver(
  bodyContainer,
  ([{ boundingClientRect }]) => {
    isScrolled.value = boundingClientRect.top < -100
  },
  {
    threshold: [0, 1],
  },
)
const productNames = computed(() =>
  products.value?.length >= 2
    ? [products.value[0].title, products.value[1].title]
    : null,
)
useModelsComparisonHead(productNames)

useAddCanonicalLink()
</script>
